/**
 * LayoutService.ts
 * Service pour gérer la sauvegarde et le chargement des mises en page
 */
import { supabase, isSupabaseConfigured } from './SupabaseService';
import { logger } from '../utils/ProfessionalLogger';
import { AppError } from '../utils/AppError';

export interface FrameData {
  x: number;
  y: number;
  width: number;
  height: number;
  photoIndex: number;
}

export interface LayoutData {
  id?: string;
  name: string;
  frameCount: number;
  frames: FrameData[];
  format: {
    width: number;
    height: number;
  };
  createdAt?: number;
  updatedAt?: number;
}

export class LayoutService {
  private static instance: LayoutService | null = null;

  public static getInstance(): LayoutService {
    if (!LayoutService.instance) {
      LayoutService.instance = new LayoutService();
    }
    return LayoutService.instance;
  }

  /**
   * Sauvegarde une mise en page dans Supabase
   */
  public async saveLayout(layout: LayoutData): Promise<string> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new AppError('SUPABASE_ERROR', 'Supabase non configuré');
    }

    try {
      // Récupérer l'utilisateur actuel
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new AppError('AUTH_ERROR', 'Utilisateur non authentifié');
      }

      // P3 : cast en any pour matcher le type Supabase auto-généré (Json vs FrameData[]).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const layoutData: any = {
        user_id: user.id,
        name: layout.name,
        frame_count: layout.frameCount,
        frames: layout.frames,
        format_width: layout.format.width,
        format_height: layout.format.height,
        updated_at: new Date().toISOString(),
      };

      // Si l'ID existe, mettre à jour, sinon créer
      if (layout.id) {
        const { error } = await supabase
          .from('template_editor_layouts')
          .update(layoutData)
          .eq('id', layout.id)
          .eq('user_id', user.id)
          .select()
          .single();

        if (error) {
          throw new AppError('SUPABASE_ERROR', error.message, { error });
        }

        logger.info(`✅ Mise en page mise à jour: ${layout.name}`);
        return layout.id;
      } else {
        const { data, error } = await supabase
          .from('template_editor_layouts')
          .insert({
            ...layoutData,
            created_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (error) {
          throw new AppError('SUPABASE_ERROR', error.message, { error });
        }

        logger.info(`✅ Mise en page sauvegardée: ${layout.name}`);
        return data.id;
      }
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde de la mise en page:', error);
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'SAVE_ERROR',
        'Erreur lors de la sauvegarde de la mise en page',
        {},
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Charge une mise en page depuis Supabase
   */
  public async loadLayout(layoutId: string): Promise<LayoutData> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new AppError('SUPABASE_ERROR', 'Supabase non configuré');
    }

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new AppError('AUTH_ERROR', 'Utilisateur non authentifié');
      }

      const { data, error } = await supabase
        .from('template_editor_layouts')
        .select('*')
        .eq('id', layoutId)
        .eq('user_id', user.id)
        .single();

      if (error || !data) {
        throw new AppError('NOT_FOUND', 'Mise en page non trouvée');
      }

      const layout: LayoutData = {
        id: data.id,
        name: data.name,
        frameCount: data.frame_count,
        // P3 : `frames` est typé Json côté Supabase, on cast en FrameData[].
        frames: data.frames as unknown as FrameData[],
        format: {
          width: data.format_width,
          height: data.format_height,
        },
        createdAt: new Date(data.created_at).getTime(),
        updatedAt: new Date(data.updated_at).getTime(),
      };

      logger.info(`📂 Mise en page chargée: ${layout.name}`);
      return layout;
    } catch (error) {
      logger.error('Erreur lors du chargement de la mise en page:', error);
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'LOAD_ERROR',
        'Erreur lors du chargement de la mise en page',
        {},
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Liste toutes les mises en page de l'utilisateur
   */
  public async getUserLayouts(): Promise<LayoutData[]> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new AppError('SUPABASE_ERROR', 'Supabase non configuré');
    }

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new AppError('AUTH_ERROR', 'Utilisateur non authentifié');
      }

      const { data, error } = await supabase
        .from('template_editor_layouts')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) {
        throw new AppError('SUPABASE_ERROR', error.message, { error });
      }

      interface APILayoutItem {
        id: string;
        name: string;
        frame_count?: number;
        [key: string]: unknown;
      }
      // P3 : double cast pour matcher le type union Supabase auto-généré.
      const layouts: LayoutData[] = ((data ?? []) as unknown as APILayoutItem[]).map(item => ({
        id: item.id,
        name: item.name,
        frameCount: item.frame_count ?? 0,
        frames: item.frames as unknown as FrameData[],
        format: {
          width: (item.format_width as number) ?? 0,
          height: (item.format_height as number) ?? 0,
        },
        createdAt: new Date(item.created_at as string).getTime(),
        updatedAt: new Date(item.updated_at as string).getTime(),
      }));

      logger.info(`📂 ${layouts.length} mise(s) en page chargée(s)`);
      return layouts;
    } catch (error) {
      logger.error('Erreur lors du chargement des mises en page:', error);
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'LOAD_ERROR',
        'Erreur lors du chargement des mises en page',
        {},
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Supprime une mise en page
   */
  public async deleteLayout(layoutId: string): Promise<boolean> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new AppError('SUPABASE_ERROR', 'Supabase non configuré');
    }

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new AppError('AUTH_ERROR', 'Utilisateur non authentifié');
      }

      const { error } = await supabase
        .from('template_editor_layouts')
        .delete()
        .eq('id', layoutId)
        .eq('user_id', user.id);

      if (error) {
        throw new AppError('SUPABASE_ERROR', error.message, { error });
      }

      logger.info(`🗑️ Mise en page supprimée: ${layoutId}`);
      return true;
    } catch (error) {
      logger.error('Erreur lors de la suppression de la mise en page:', error);
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'DELETE_ERROR',
        'Erreur lors de la suppression de la mise en page',
        {},
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }
}

// Export singleton instance
export const layoutService = LayoutService.getInstance();
